import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  EyeIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  UserIcon,
  FunnelIcon,
  ChevronUpDownIcon,
  PhoneIcon,
  PlusIcon,
  DocumentDuplicateIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import {
  getAllApplications,
  getCachedApplications,
  exportApplicationsToExcel,
  updateApplicationStatus,
  deleteApplication,
  onAuthStateChange
} from '../services/firebaseService';
import { Application } from '../types';
import { formatDateTime, ODISHA_DISTRICTS } from '../utils/helpers';
import { sendDisqualificationEmail } from '../services/emailService';
import AdminHeader from '../components/admin/AdminHeader';

const ApplicationsManagementPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const urlQuery = searchParams.get('q') || '';
  const urlStatus = searchParams.get('status') || 'all';

  const [activeTab, setActiveTab] = useState<'new' | 'review' | 'completed' | 'all' | 'enquiries'>('new');
  const [applications, setApplications] = useState<Application[]>([]);
  const [filteredApplications, setFilteredApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(urlQuery);
  const [statusFilter, setStatusFilter] = useState(urlStatus);
  const [districtFilter, setDistrictFilter] = useState('all');
  const [sortBy, setSortBy] = useState('submittedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [role, setRole] = useState<string>('central-admin');
  const [showFiltersOnMobile, setShowFiltersOnMobile] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Enquiries state
  const [enquiries, setEnquiries] = useState<any[]>([]);
  const [loadingEnquiries, setLoadingEnquiries] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    // 1. Instant Cache Hydration: Render immediately if cached (< 5ms)
    const cached = getCachedApplications();
    const stored = localStorage.getItem('adminUser');
    const adminUser = stored ? JSON.parse(stored) : null;
    if (cached && cached.length > 0) {
      const userRole = adminUser?.role || 'admin';
      setRole(userRole);
      const scoped = applyRoleScope(cached, userRole);
      setApplications(scoped);
      setFilteredApplications(scoped);
      setLoading(false);
    }

    if (!stored) {
      navigate('/admin/login');
      return;
    }

    const fetchApplications = async () => {
      const userRole = adminUser?.role || 'admin';
      setRole(userRole);

      try {
        const apps = await getAllApplications();
        const scoped = applyRoleScope(apps, userRole);
        setApplications(scoped);
        setFilteredApplications(scoped);
      } catch (error) {
        console.error('Error fetching applications:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();

    const unsubscribe = onAuthStateChange((user) => {
      if (!user) {
        if (!localStorage.getItem('adminUser')) {
          navigate('/admin/login');
        }
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const fetchAllEnquiries = async () => {
    setLoadingEnquiries(true);
    try {
      const { getEnquiries } = await import('../services/firebaseService');
      const data = await getEnquiries();
      setEnquiries(data);
    } catch (err) {
      console.error('Error fetching enquiries:', err);
    } finally {
      setLoadingEnquiries(false);
    }
  };

  const handleDeleteEnquiry = async (enquiryId: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete the enquiry from ${name}?`)) return;
    try {
      const { deleteEnquiry } = await import('../services/firebaseService');
      await deleteEnquiry(enquiryId);
      alert('Enquiry deleted successfully!');
      fetchAllEnquiries();
    } catch (error) {
      console.error('Error deleting enquiry:', error);
      alert('Failed to delete enquiry.');
    }
  };

  useEffect(() => {
    if (activeTab === 'enquiries') {
      fetchAllEnquiries();
    }
  }, [activeTab]);

  const applyRoleScope = (apps: Application[], userRole: string) => {
    switch (userRole) {
      case 'candidate':
        return [];
      default:
        return apps;
    }
  };

  const handleCopyId = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  useEffect(() => {
    let filtered = [...applications];

    // Apply tab filter
    if (activeTab === 'new') {
      filtered = filtered.filter(app => app.status === 'submitted');
    } else if (activeTab === 'review') {
      filtered = filtered.filter(app => ['under_review', 'on_hold', 'clarification_requested'].includes(app.status));
    } else if (activeTab === 'completed') {
      filtered = filtered.filter(app => ['verified', 'rejected'].includes(app.status));
    }

    // Apply search filter
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(app =>
        `${app.firstName || ''} ${app.lastName || ''}`.toLowerCase().includes(q) ||
        (app.emailId || '').toLowerCase().includes(q) ||
        (app.mobileNumber || '').includes(searchTerm) ||
        (app.id || '').toLowerCase().includes(q) ||
        (app.registrationNumber || '').toLowerCase().includes(q)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(app => app.status === statusFilter);
    }

    // Apply district filter
    if (districtFilter !== 'all') {
      filtered = filtered.filter(app => app.address?.district === districtFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any = a[sortBy as keyof Application];
      let bValue: any = b[sortBy as keyof Application];

      if (sortBy === 'submittedAt' || sortBy === 'reviewedAt') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      } else if (typeof aValue === 'string' || typeof bValue === 'string') {
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
      } else {
        aValue = aValue ?? 0;
        bValue = bValue ?? 0;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredApplications(filtered);
    setCurrentPage(1);
  }, [applications, searchTerm, statusFilter, districtFilter, sortBy, sortOrder, activeTab]);

  const handleExportExcel = async () => {
    try {
      await exportApplicationsToExcel();
    } catch (error) {
      console.error('Error exporting applications:', error);
      alert('Error exporting applications. Please try again.');
    }
  };

  const getStatusBadge = (status: string) => {
    const config: Record<string, { bg: string; text: string; dot: string; label: string }> = {
      submitted: { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-700', dot: 'bg-blue-500', label: 'New Submission' },
      under_review: { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-800', dot: 'bg-amber-500', label: 'Under Review' },
      verified: { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-800', dot: 'bg-emerald-500', label: 'Verified' },
      rejected: { bg: 'bg-rose-50 border-rose-200', text: 'text-rose-800', dot: 'bg-rose-500', label: 'Rejected' },
      on_hold: { bg: 'bg-slate-100 border-slate-200', text: 'text-slate-800', dot: 'bg-slate-500', label: 'On Hold' },
      clarification_requested: { bg: 'bg-orange-50 border-orange-200', text: 'text-orange-800', dot: 'bg-orange-500', label: 'Clarification Needed' }
    };

    const item = config[status] || { bg: 'bg-slate-100 border-slate-200', text: 'text-slate-700', dot: 'bg-slate-400', label: status.replace(/_/g, ' ') };
    return (
      <span className={`inline-flex items-center space-x-1.5 px-2.5 py-1 text-[11px] font-bold rounded-full border ${item.bg} ${item.text}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${item.dot}`} />
        <span>{item.label}</span>
      </span>
    );
  };

  const refresh = async () => {
    const apps = await getAllApplications();
    const scoped = applyRoleScope(apps, role);
    setApplications(scoped);
    setFilteredApplications(scoped);
  };

  const handleDecision = async (applicationId: string, status: Application['status']) => {
    const remark = window.prompt(`Enter remark for status change to ${status.replace('_', ' ')}:`, '');
    if (remark === null) return;

    try {
      const stored = localStorage.getItem('adminUser');
      const adminUser = stored ? JSON.parse(stored) : { email: 'admin' };

      // 1. Optimistic Local Update (0ms latency, zero extra reads)
      setApplications(prev => prev.map(a => 
        a.id === applicationId ? { ...a, status, reviewedAt: new Date() } : a
      ));
      setFilteredApplications(prev => prev.map(a => 
        a.id === applicationId ? { ...a, status, reviewedAt: new Date() } : a
      ));

      // 2. Persist to Firestore & in-place cache
      await updateApplicationStatus(
        applicationId,
        status,
        remark || '',
        adminUser.email || 'admin',
        adminUser.role || 'admin'
      );
    } catch (error) {
      console.error('Error updating application status:', error);
      alert('Failed to update status.');
      // Revert if error
      refresh();
    }
  };

  const handleQuickDelete = async (application: Application) => {
    const applicantName = `${application.firstName} ${application.lastName}`;
    const applicationId = application.id;

    const secretCode = window.prompt(`To delete the application for ${applicantName} (ID: ${applicationId}), please enter the secret code:`);
    if (secretCode === null) return;
    if (secretCode !== 'KSAI2026') {
      alert('Incorrect secret code. Deletion cancelled.');
      return;
    }

    const reason = window.prompt(`Enter the reason for disqualification/deletion of this registration:`);
    if (reason === null) return;
    if (!reason.trim()) {
      alert('A reason is required to notify the applicant. Deletion cancelled.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the application for ${applicantName}?\n\nID: ${applicationId}\n\nThis action cannot be undone and will send a disqualification email.`)) return;

    try {
      // 1. Optimistic Local Removal (0ms latency)
      setApplications(prev => prev.filter(a => a.id !== applicationId));
      setFilteredApplications(prev => prev.filter(a => a.id !== applicationId));

      await sendDisqualificationEmail(application, reason);
      await deleteApplication(applicationId);
      alert('Application disqualified and deleted successfully! Notification email sent.');
    } catch (error) {
      console.error('Error deleting application:', error);
      alert('Error deleting application. Please try again.');
      refresh();
    }
  };

  // Pagination calculation
  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentApplications = filteredApplications.slice(startIndex, endIndex);

  // Active filter count for badge
  const activeFiltersCount = (statusFilter !== 'all' ? 1 : 0) + (districtFilter !== 'all' ? 1 : 0) + (searchTerm ? 1 : 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <div className="w-16 h-16 rounded-full border-4 border-primary-200 border-t-primary-600 animate-spin" />
        <p className="mt-4 text-sm font-bold text-slate-800">Loading Registrations...</p>
      </div>
    );
  }

  const customHeaderActions = (
    <button
      onClick={() => navigate('/apply')}
      className="bg-primary-600 hover:bg-primary-700 text-white px-3.5 py-1.5 sm:py-2 rounded-xl text-xs font-bold transition flex items-center space-x-1 shadow-sm"
    >
      <PlusIcon className="w-4 h-4" />
      <span>New Application</span>
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Unified Admin Navigation Header */}
      <AdminHeader
        activeNav="applications"
        title="Registrations Management"
        subtitle="Verification, ATS Decisions & Enquiries"
        actions={customHeaderActions}
        onExportExcel={handleExportExcel}
      />

      <main className="flex-1 max-w-7xl w-full mx-auto py-5 sm:py-8 px-3 sm:px-6 lg:px-8 pb-24 lg:pb-12 space-y-5">

        {/* Responsive Horizontal Tabs Bar */}
        <div className="bg-white rounded-2xl sm:rounded-3xl p-1.5 sm:p-2 border border-slate-200/80 shadow-sm flex items-center overflow-x-auto no-scrollbar scroll-smooth space-x-1">
          <button
            onClick={() => setActiveTab('new')}
            className={`flex-shrink-0 flex items-center space-x-2 px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs font-bold transition ${activeTab === 'new'
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
          >
            <span>New Applications</span>
            <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-black ${activeTab === 'new' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-700'}`}>
              {applications.filter(a => a.status === 'submitted').length}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('review')}
            className={`flex-shrink-0 flex items-center space-x-2 px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs font-bold transition ${activeTab === 'review'
                ? 'bg-amber-600 text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
          >
            <span>In Review</span>
            <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-black ${activeTab === 'review' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-700'}`}>
              {applications.filter(a => ['under_review', 'on_hold', 'clarification_requested'].includes(a.status)).length}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('completed')}
            className={`flex-shrink-0 flex items-center space-x-2 px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs font-bold transition ${activeTab === 'completed'
                ? 'bg-emerald-600 text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
          >
            <span>Completed</span>
            <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-black ${activeTab === 'completed' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-700'}`}>
              {applications.filter(a => ['verified', 'rejected'].includes(a.status)).length}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('all')}
            className={`flex-shrink-0 flex items-center space-x-2 px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs font-bold transition ${activeTab === 'all'
                ? 'bg-slate-900 text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
          >
            <span>All Registrations</span>
            <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-black ${activeTab === 'all' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-700'}`}>
              {applications.length}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('enquiries')}
            className={`flex-shrink-0 flex items-center space-x-2 px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs font-bold transition ${activeTab === 'enquiries'
                ? 'bg-indigo-600 text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
          >
            <span>Public Enquiries</span>
            {enquiries.length > 0 && (
              <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-black ${activeTab === 'enquiries' ? 'bg-white/20 text-white' : 'bg-indigo-100 text-indigo-800'}`}>
                {enquiries.length}
              </span>
            )}
          </button>
        </div>

        {/* ── Search, Filters & Mobile Drawer ── */}
        <div className="bg-white rounded-3xl p-4 sm:p-5 shadow-sm border border-slate-200/80 space-y-4">
          <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3">
            {/* Quick Search */}
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, ID, phone, email..."
                className="w-full pl-11 pr-10 py-2.5 rounded-2xl bg-slate-50 border border-slate-200 focus:bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 text-xs sm:text-sm font-medium transition"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 bg-slate-200 rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-bold"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Quick Filter Actions */}
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={() => setShowFiltersOnMobile(!showFiltersOnMobile)}
                className={`flex-1 md:flex-none px-3.5 py-2.5 rounded-2xl text-xs font-bold border transition flex items-center justify-center space-x-1.5 ${activeFiltersCount > 0
                    ? 'bg-primary-50 border-primary-300 text-primary-800'
                    : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
                  }`}
              >
                <FunnelIcon className="w-4 h-4" />
                <span>Filters</span>
                {activeFiltersCount > 0 && (
                  <span className="w-5 h-5 rounded-full bg-primary-600 text-white text-[10px] font-extrabold flex items-center justify-center">
                    {activeFiltersCount}
                  </span>
                )}
              </button>

              <button
                type="button"
                onClick={handleExportExcel}
                className="px-3.5 py-2.5 rounded-2xl text-xs font-bold bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200 transition flex items-center space-x-1.5 shadow-sm"
              >
                <ArrowDownTrayIcon className="w-4 h-4 text-emerald-600" />
                <span className="hidden sm:inline">Export Excel</span>
              </button>
            </div>
          </div>

          {/* Quick Filter Pills (Status) */}
          <div className="flex items-center space-x-1.5 overflow-x-auto no-scrollbar pt-1">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mr-1 flex-shrink-0">
              Quick Filter:
            </span>
            {[
              { id: 'all', label: 'All Status' },
              { id: 'submitted', label: 'New' },
              { id: 'under_review', label: 'Review' },
              { id: 'verified', label: 'Verified' },
              { id: 'rejected', label: 'Rejected' },
              { id: 'on_hold', label: 'On Hold' }
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setStatusFilter(f.id)}
                className={`px-3 py-1 rounded-xl text-xs font-bold transition flex-shrink-0 ${statusFilter === f.id
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'bg-slate-100 hover:bg-slate-200/80 text-slate-600'
                  }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Collapsible Detailed Filters (District, Sort, Direction) */}
          {(showFiltersOnMobile || window.innerWidth >= 1024) && (
            <div className="pt-3 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-3 gap-3 animate-in fade-in duration-150">
              {/* District Filter */}
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                  District
                </label>
                <select
                  value={districtFilter}
                  onChange={(e) => setDistrictFilter(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs font-semibold focus:bg-white focus:border-primary-500 focus:ring-2 focus:ring-primary-500/10"
                >
                  <option value="all">All Districts</option>
                  {Object.keys(ODISHA_DISTRICTS).map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>

              {/* Sort By */}
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                  Sort By
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs font-semibold focus:bg-white focus:border-primary-500 focus:ring-2 focus:ring-primary-500/10"
                >
                  <option value="submittedAt">Submission Date</option>
                  <option value="reviewedAt">Review Date</option>
                  <option value="firstName">First Name</option>
                  <option value="id">Registration ID</option>
                </select>
              </div>

              {/* Sort Order */}
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                  Direction
                </label>
                <button
                  type="button"
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="w-full py-2 px-3 rounded-xl bg-slate-50 border border-slate-200 text-xs font-bold text-slate-700 hover:bg-slate-100 transition flex items-center justify-between"
                >
                  <span>{sortOrder === 'asc' ? 'Ascending (Oldest first)' : 'Descending (Newest first)'}</span>
                  <ChevronUpDownIcon className="w-4 h-4 text-slate-500" />
                </button>
              </div>
            </div>
          )}

          {/* Quick Counter Banner */}
          <div className="flex items-center justify-between text-xs font-semibold text-slate-500 pt-1">
            <span>
              Showing <strong className="text-slate-800">{filteredApplications.length}</strong> matching candidates
            </span>
            {activeFiltersCount > 0 && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setDistrictFilter('all');
                }}
                className="text-primary-600 hover:text-primary-800 font-bold"
              >
                Reset All Filters
              </button>
            )}
          </div>
        </div>

        {/* ── Tab: Public Enquiries View ── */}
        {activeTab === 'enquiries' ? (
          <div className="bg-white rounded-3xl shadow-sm border border-slate-200/80 overflow-hidden">
            {loadingEnquiries ? (
              <div className="px-6 py-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600 mx-auto" />
                <p className="text-slate-500 mt-4 font-bold text-xs">Loading public enquiries...</p>
              </div>
            ) : enquiries.length === 0 ? (
              <div className="p-12 text-center">
                <UserIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <h3 className="text-base font-bold text-slate-900 mb-1">No Enquiries Found</h3>
                <p className="text-xs text-slate-500">No public queries submitted through the homepage contact form.</p>
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-100">
                    <thead className="bg-slate-50/80">
                      <tr>
                        <th className="px-6 py-3.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">Candidate Details</th>
                        <th className="px-6 py-3.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">Subject</th>
                        <th className="px-6 py-3.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">Message</th>
                        <th className="px-6 py-3.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">Submitted</th>
                        <th className="px-6 py-3.5 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {enquiries.map((enq) => (
                        <tr key={enq.id} className="hover:bg-slate-50/80 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <p className="text-xs font-bold text-slate-900">{enq.name}</p>
                            <p className="text-[11px] text-slate-500 font-medium">{enq.email}</p>
                            <p className="text-[11px] text-slate-500 font-mono">{enq.mobileNumber}</p>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex px-2.5 py-1 text-[11px] font-bold rounded-full bg-primary-50 border border-primary-200 text-primary-800">
                              {enq.subject}
                            </span>
                          </td>
                          <td className="px-6 py-4 max-w-md">
                            <p className="text-xs text-slate-700 leading-relaxed font-medium whitespace-pre-wrap line-clamp-3">
                              {enq.message}
                            </p>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-500 font-medium">
                            {enq.submittedAt ? new Date(enq.submittedAt).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            {role === 'super_admin' && (
                              <button
                                onClick={() => handleDeleteEnquiry(enq.id, enq.name)}
                                className="text-rose-600 hover:text-rose-800 text-xs font-bold inline-flex items-center space-x-1"
                              >
                                <TrashIcon className="w-4 h-4" />
                                <span>Delete</span>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards View for Enquiries */}
                <div className="md:hidden divide-y divide-slate-100">
                  {enquiries.map((enq) => (
                    <div key={enq.id} className="p-4 space-y-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-xs font-bold text-slate-900">{enq.name}</p>
                          <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-primary-50 text-primary-800 border border-primary-200">
                            {enq.subject}
                          </span>
                        </div>
                        {role === 'super_admin' && (
                          <button
                            onClick={() => handleDeleteEnquiry(enq.id, enq.name)}
                            className="p-1.5 text-rose-500 hover:text-rose-700 bg-rose-50 rounded-xl"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      <p className="text-xs text-slate-700 bg-slate-50 p-3 rounded-2xl border border-slate-100">
                        {enq.message}
                      </p>

                      <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1">
                        <a href={`tel:${enq.mobileNumber}`} className="text-primary-600 font-bold flex items-center space-x-1">
                          <PhoneIcon className="w-3.5 h-3.5" />
                          <span>{enq.mobileNumber}</span>
                        </a>
                        <a href={`mailto:${enq.email}`} className="text-slate-600 font-medium truncate max-w-[150px]">
                          {enq.email}
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          /* ── Tab: Applications Data Grid & Mobile Cards ── */
          <div className="bg-white rounded-3xl shadow-sm border border-slate-200/80 overflow-hidden">
            {filteredApplications.length === 0 ? (
              <div className="p-12 text-center">
                <DocumentDuplicateIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <h3 className="text-base font-bold text-slate-900 mb-1">No Matching Registrations</h3>
                <p className="text-xs text-slate-500 max-w-sm mx-auto">
                  No records found matching current tab and filter criteria. Try clearing search or selecting a different status.
                </p>
              </div>
            ) : (
              <>
                {/* Desktop View Table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-100">
                    <thead className="bg-slate-50/80">
                      <tr>
                        <th className="px-6 py-3.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                          Applicant &amp; Reg ID
                        </th>
                        <th className="px-6 py-3.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                          Contact
                        </th>
                        <th className="px-6 py-3.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                          Location
                        </th>
                        <th className="px-6 py-3.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                          Submitted
                        </th>
                        <th className="px-6 py-3.5 text-right text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {currentApplications.map((app) => (
                        <tr key={app.id} className="hover:bg-slate-50/80 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-3">
                              <div className="w-9 h-9 rounded-xl bg-primary-100 text-primary-700 font-bold text-xs flex items-center justify-center uppercase">
                                {app.firstName?.[0] || 'A'}
                              </div>
                              <div>
                                <p className="text-xs font-bold text-slate-900">
                                  {app.firstName} {app.lastName}
                                </p>
                                <div className="flex items-center space-x-1.5 mt-0.5">
                                  <span className="text-[11px] text-slate-500 font-mono font-medium">
                                    {app.registrationNumber || app.id}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={(e) => handleCopyId(app.registrationNumber || app.id, e)}
                                    className="text-slate-400 hover:text-slate-600 transition"
                                    title="Copy ID"
                                  >
                                    {copiedId === (app.registrationNumber || app.id) ? (
                                      <CheckIcon className="w-3.5 h-3.5 text-emerald-600" />
                                    ) : (
                                      <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            <p className="text-xs text-slate-800 font-medium">{app.emailId}</p>
                            <p className="text-[11px] text-slate-500 font-mono">{app.mobileNumber}</p>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            <p className="text-xs font-semibold text-slate-800">{app.address?.district || 'N/A'}</p>
                            <p className="text-[11px] text-slate-400">{app.address?.block || '—'}</p>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(app.status)}
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-500 font-medium">
                            <div>{formatDateTime(app.submittedAt)}</div>
                            {app.reviewedAt && (
                              <div className="text-[10px] text-slate-400 mt-0.5">
                                Reviewed: {formatDateTime(app.reviewedAt)}
                              </div>
                            )}
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="flex items-center justify-end space-x-1.5">
                              <button
                                onClick={() => navigate(`/admin/applications/${app.id}`)}
                                className="px-3 py-1.5 bg-primary-50 hover:bg-primary-100 text-primary-700 font-bold rounded-xl text-xs transition inline-flex items-center space-x-1"
                              >
                                <EyeIcon className="w-3.5 h-3.5" />
                                <span>Inspect</span>
                              </button>

                              {/* ATS Quick Action Buttons for central admin */}
                              {['admin', 'super_admin'].includes(role) && app.status === 'submitted' && (
                                <button
                                  onClick={() => handleDecision(app.id, 'under_review')}
                                  className="px-2.5 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-800 font-bold rounded-xl text-xs transition"
                                  title="Forward to Review"
                                >
                                  Review
                                </button>
                              )}

                              {['admin', 'super_admin'].includes(role) && (
                                <button
                                  onClick={() => handleQuickDelete(app)}
                                  className="p-1.5 text-slate-400 hover:text-rose-600 rounded-lg transition"
                                  title="Disqualify & Delete"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Touch Cards View (Optimized for Phones) */}
                <div className="md:hidden divide-y divide-slate-100">
                  {currentApplications.map((app) => (
                    <div key={app.id} className="p-4 space-y-3">
                      {/* Card Header */}
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center space-x-3 min-w-0">
                          <div className="w-10 h-10 rounded-2xl bg-primary-100 text-primary-700 font-bold text-sm flex items-center justify-center flex-shrink-0 uppercase shadow-sm">
                            {app.firstName?.[0] || 'A'}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-slate-900 truncate">
                              {app.firstName} {app.lastName}
                            </p>
                            <div className="flex items-center space-x-1.5 mt-0.5">
                              <span className="text-[11px] text-slate-500 font-mono font-medium truncate">
                                {app.registrationNumber || app.id}
                              </span>
                              <button
                                type="button"
                                onClick={(e) => handleCopyId(app.registrationNumber || app.id, e)}
                                className="text-slate-400 hover:text-slate-600"
                              >
                                {copiedId === (app.registrationNumber || app.id) ? (
                                  <CheckIcon className="w-3.5 h-3.5 text-emerald-600" />
                                ) : (
                                  <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="flex-shrink-0">
                          {getStatusBadge(app.status)}
                        </div>
                      </div>

                      {/* Contact & Location Strip */}
                      <div className="grid grid-cols-2 gap-2 p-2.5 bg-slate-50 rounded-2xl border border-slate-100 text-[11px]">
                        <div>
                          <p className="text-slate-400 font-bold uppercase text-[9px]">Location</p>
                          <p className="font-semibold text-slate-800 truncate">📍 {app.address?.district || 'N/A'}</p>
                          <p className="text-slate-500 text-[10px] truncate">{app.address?.block || '—'}</p>
                        </div>
                        <div>
                          <p className="text-slate-400 font-bold uppercase text-[9px]">Phone</p>
                          <a href={`tel:${app.mobileNumber}`} className="font-bold text-primary-600 truncate block">
                            📞 {app.mobileNumber}
                          </a>
                          <span className="text-slate-400 text-[10px] block truncate">
                            📅 {formatDateTime(app.submittedAt).split(',')[0]}
                          </span>
                        </div>
                      </div>

                      {/* Mobile Action Buttons */}
                      <div className="flex items-center space-x-2 pt-1">
                        <button
                          type="button"
                          onClick={() => navigate(`/admin/applications/${app.id}`)}
                          className="flex-1 bg-primary-600 hover:bg-primary-700 active:scale-98 text-white font-bold py-2.5 px-4 rounded-xl text-xs transition flex items-center justify-center space-x-1.5 shadow-sm"
                        >
                          <EyeIcon className="w-4 h-4" />
                          <span>Review Application</span>
                        </button>

                        {['admin', 'super_admin'].includes(role) && (
                          <button
                            type="button"
                            onClick={() => handleQuickDelete(app)}
                            className="p-2.5 text-slate-400 hover:text-rose-600 bg-slate-100 hover:bg-rose-50 rounded-xl transition"
                            title="Disqualify"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* ── Responsive Pagination Bar ── */}
                {totalPages > 1 && (
                  <div className="bg-slate-50/80 px-4 py-3.5 flex flex-col sm:flex-row items-center justify-between border-t border-slate-100 gap-3">
                    <p className="text-xs font-semibold text-slate-600 text-center sm:text-left">
                      Showing <span className="font-bold text-slate-900">{startIndex + 1}</span> to{' '}
                      <span className="font-bold text-slate-900">{Math.min(endIndex, filteredApplications.length)}</span> of{' '}
                      <span className="font-bold text-slate-900">{filteredApplications.length}</span> candidates
                    </p>

                    <div className="flex items-center space-x-1.5">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
                      >
                        Previous
                      </button>

                      <div className="flex items-center space-x-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          const pageNum = i + 1;
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`w-7 h-7 rounded-xl text-xs font-bold transition ${pageNum === currentPage
                                  ? 'bg-primary-600 text-white shadow-sm'
                                  : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-100'
                                }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                        {totalPages > 5 && (
                          <span className="text-xs text-slate-400 px-1 font-bold">... {totalPages}</span>
                        )}
                      </div>

                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default ApplicationsManagementPage;
